import { Lexer } from './lexer'
import type {
  TokenType,
  Token,
  AstNode,
  ComparisonNode,
  ValueNode,
} from './types'

// Parser for a pragmatic Lucene-like subset used by ViewState filters
export class Parser {
  private current: Token
  private readonly lexer: Lexer

  constructor(input: string) {
    this.lexer = new Lexer(input)
    this.current = this.lexer.next()
  }

  // Map comparator token types to their string operator representation
  static readonly COMPARATOR_TOKEN_TO_OPERATOR: Partial<
    Record<TokenType, ComparisonNode['op']>
  > = {
    COLON: ':',
    MORE_THAN_EQUAL: '>=',
    MORE_THAN: '>',
    LESS_THAN_EQUAL: '<=',
    LESS_THAN: '<',
  }

  // Reverse map: operator symbol -> comparator token type
  static readonly OPERATOR_SYMBOL_TO_TOKEN: Partial<
    Record<ComparisonNode['op'], TokenType>
  > = Object.fromEntries(
    Object.entries(Parser.COMPARATOR_TOKEN_TO_OPERATOR).map(
      ([token, symbol]) => [symbol, token as TokenType],
    ),
  )

  // Reverse mapping of single-char token types back to their literal character
  // Used when building scalar values to avoid hardcoded character cases
  private static readonly TOKEN_TYPE_TO_CHAR: Partial<
    Record<TokenType, string>
  > = Object.fromEntries(
    Object.entries(Lexer.SINGLE_CHAR_TOKEN_MAP).map(([ch, t]) => [t, ch]),
  )

  private eat(type: TokenType): Token {
    if (this.current.type !== type) {
      throw new Error(
        `Expected ${type} at ${this.current.pos}, got ${this.current.type}`,
      )
    }
    const consumedToken = this.current
    this.current = this.lexer.next()
    return consumedToken
  }

  private match(type: TokenType): boolean {
    if (this.current.type === type) {
      this.current = this.lexer.next()
      return true
    }
    return false
  }

  parse(): AstNode {
    const node = this.parseOr()
    if (this.current.type !== 'EOF') {
      throw new Error(
        `Unexpected token ${this.current.type} at ${this.current.pos}`,
      )
    }
    return node
  }

  private parseOr(): AstNode {
    let node = this.parseAnd()
    while (this.current.type === 'OR') {
      this.eat('OR')
      const right = this.parseAnd()
      node = { kind: 'BINARY', op: 'OR', left: node, right }
    }
    return node
  }

  private parseAnd(): AstNode {
    let node = this.parseUnary()
    while (this.current.type === 'AND' || this.isStartOfPrimary()) {
      if (this.current.type === 'AND') {
        this.eat('AND')
      }
      const right = this.parseUnary()
      node = { kind: 'BINARY', op: 'AND', left: node, right }
    }
    return node
  }

  private parseUnary(): AstNode {
    if (this.current.type === 'NOT' || this.current.type === 'MINUS') {
      this.eat(this.current.type)
      const node = this.parseUnary()
      return { kind: 'NOT', node }
    }
    return this.parsePrimary()
  }

  private isStartOfPrimary(): boolean {
    return this.current.type === 'LPAREN' || this.current.type === 'IDENTIFIER'
  }

  private parsePrimary(): AstNode {
    if (this.match('LPAREN')) {
      const node = this.parseOr()
      this.eat('RPAREN')
      return { kind: 'GROUP', node }
    }
    return this.parseComparison()
  }

  private parseComparison(): ComparisonNode {
    const keyTok = this.eat('IDENTIFIER')
    const tokenType = this.current.type
    const mappedOp = Parser.COMPARATOR_TOKEN_TO_OPERATOR[tokenType]
    if (!mappedOp) {
      throw new Error(`Expected comparator after key at ${this.current.pos}`)
    }
    this.eat(tokenType)
    const op: ComparisonNode['op'] = mappedOp

    const value = this.parseValue(op)
    return { kind: 'COMPARISON', key: keyTok.value as string, op, value }
  }

  private parseValue(op: ComparisonNode['op']): ValueNode {
    if (op === ':' && this.match('LPAREN')) {
      const parseValues = (): string[] => {
        const value = this.parseScalar()
        if (this.current.type === 'COMMA') {
          this.eat('COMMA')
          return [value, ...parseValues()]
        }
        return [value]
      }

      const values = parseValues()
      this.eat('RPAREN')
      return { kind: 'LIST', values }
    }

    return { kind: 'SCALAR', value: this.parseScalar() }
  }

  private parseScalar(): string {
    let buf = ''
    const append = (segment?: string) => {
      if (segment) buf += segment
    }
    const IN_PREFIX_ESCAPED = String.raw`$in\[`

    const isInPrefix = (text?: string) =>
      !!text && (text.startsWith('$in[') || text.startsWith(IN_PREFIX_ESCAPED))

    const valueToken = (tokenParam: Token) => {
      switch (tokenParam.type) {
        case 'STRING':
        case 'IDENTIFIER':
        case 'NUMBER':
          append(tokenParam.value)
          return true
        case 'COMMA': {
          // Allow commas when parsing $in[...] payload
          const inIn =
            buf.includes('$in[') ||
            buf.includes(IN_PREFIX_ESCAPED) ||
            (this.current.type === 'IDENTIFIER' &&
              isInPrefix(this.current.value))
          if (inIn) {
            append(',')
            return true
          }
          return false
        }
        default: {
          const mappedChar = Parser.TOKEN_TYPE_TO_CHAR[tokenParam.type]
          if (mappedChar) {
            append(mappedChar)
            return true
          }
          return false
        }
      }
    }
    const isInSyntaxToken = (token: Token) =>
      token.type === 'IDENTIFIER' && isInPrefix(token.value)

    const shouldStop = (token: Token) => {
      if (['EOF', 'RPAREN', 'AND', 'OR'].includes(token.type)) return true
      if (
        token.type === 'COMMA' &&
        !buf.includes('$in[') &&
        !buf.includes(IN_PREFIX_ESCAPED) &&
        !isInSyntaxToken(token)
      ) {
        return true
      }
      return !valueToken(token)
    }

    let currentToken = this.current

    while (!shouldStop(currentToken)) {
      this.current = this.lexer.next()
      currentToken = this.current
    }
    if (buf.length === 0) {
      const currentTokenAtError = this.current
      throw new Error(
        `Expected value at ${currentTokenAtError.pos}, got ${currentTokenAtError.type}`,
      )
    }
    return buf
  }
}
